import { useState } from 'react';
import { ArrowLeft, Trophy, Target, Award, Users, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communityService } from '../services/community.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface CommunityChallengesProps {
  onBack: () => void;
}

export function CommunityChallenges({ onBack }: CommunityChallengesProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'challenges' | 'leaderboard' | 'achievements'>(
    'challenges'
  );

  // Fetch challenges
  const {
    data: challenges = [],
    isLoading: challengesLoading,
    error: challengesError,
  } = useQuery({
    queryKey: ['challenges', user?.id],
    queryFn: () => communityService.getChallenges(),
    enabled: !!user && activeTab === 'challenges',
  });

  // Fetch leaderboard
  const {
    data: leaderboard = [],
    isLoading: leaderboardLoading,
    error: leaderboardError,
  } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => communityService.getLeaderboard({ limit: 10 }),
    enabled: activeTab === 'leaderboard',
  });

  // Fetch achievements
  const {
    data: achievements = [],
    isLoading: achievementsLoading,
    error: achievementsError,
  } = useQuery({
    queryKey: ['achievements', user?.id],
    queryFn: () => communityService.getAchievements(),
    enabled: !!user && activeTab === 'achievements',
  });

  // Join challenge mutation
  const joinMutation = useMutation({
    mutationFn: (challengeId: string) => communityService.joinChallenge(challengeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      toast.success('Challenge joined successfully!');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const isLoading =
    (activeTab === 'challenges' && challengesLoading) ||
    (activeTab === 'leaderboard' && leaderboardLoading) ||
    (activeTab === 'achievements' && achievementsLoading);

  const error =
    (activeTab === 'challenges' && challengesError) ||
    (activeTab === 'leaderboard' && leaderboardError) ||
    (activeTab === 'achievements' && achievementsError);

  if (error) {
    toast.error(handleApiError(error as Error));
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dropsugar-blue/5 via-white to-dropsugar-green/5 pb-20">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
              aria-label="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Community & Challenges</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
              <Trophy className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Join Challenges & Compete</h2>
              <p className="text-white/90">
                Participate in community challenges, compete on the leaderboard, and unlock achievements
              </p>
            </div>
          </div>
        </div>

        <div className="flex space-x-2 mb-8 bg-white rounded-xl p-2 shadow-sm border-2 border-gray-200">
          <button
            onClick={() => setActiveTab('challenges')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'challenges'
                ? 'bg-dropsugar-green text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            aria-label="View Challenges"
          >
            <Target className="w-5 h-5" />
            <span>Challenges</span>
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'leaderboard'
                ? 'bg-dropsugar-green text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            aria-label="View Leaderboard"
          >
            <TrendingUp className="w-5 h-5" />
            <span>Leaderboard</span>
          </button>
          <button
            onClick={() => setActiveTab('achievements')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'achievements'
                ? 'bg-dropsugar-green text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            aria-label="View Achievements"
          >
            <Award className="w-5 h-5" />
            <span>Achievements</span>
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <LoadingSkeleton key={i} variant="rectangular" height="150px" />
            ))}
          </div>
        ) : (
          <>
            {activeTab === 'challenges' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {challenges.length === 0 ? (
                  <div className="col-span-2 bg-white rounded-2xl p-12 border-2 border-gray-200 text-center">
                    <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">No Active Challenges</h3>
                    <p className="text-gray-600">Check back later for new challenges!</p>
                  </div>
                ) : (
                  challenges.map((challenge: any) => {
                    const progress = challenge.userProgress?.progress || 0;
                    const progressPercent = challenge.targetValue
                      ? Math.min((progress / challenge.targetValue) * 100, 100)
                      : 0;

                    return (
                      <div
                        key={challenge.id}
                        className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{challenge.name}</h3>
                            <p className="text-gray-600 text-sm mb-4">{challenge.description}</p>
                          </div>
                          <div className="p-2 bg-dropsugar-green/10 rounded-lg">
                            <Target className="w-6 h-6 text-dropsugar-green" />
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Progress</span>
                            <span className="font-semibold text-gray-900">
                              {progress} / {challenge.targetValue}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-dropsugar-green h-3 rounded-full transition-all"
                              style={{ width: `${progressPercent}%` }}
                            />
                          </div>
                        </div>

                        <button
                          onClick={() => joinMutation.mutate(challenge.id)}
                          disabled={joinMutation.isPending || challenge.userProgress}
                          className="w-full bg-dropsugar-green hover:bg-dropsugar-green-dark disabled:bg-gray-400 text-white font-semibold py-2 rounded-xl transition-all disabled:cursor-not-allowed"
                        >
                          {challenge.userProgress ? 'Joined' : 'Join Challenge'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Weekly Leaderboard</h3>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No leaderboard data yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {leaderboard.map((entry: any) => (
                      <div
                        key={entry.userId || entry.rank}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-4">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                              entry.rank === 1
                                ? 'bg-yellow-500'
                                : entry.rank === 2
                                  ? 'bg-gray-400'
                                  : entry.rank === 3
                                    ? 'bg-dropsugar-green'
                                    : 'bg-gray-300'
                            }`}
                          >
                            {entry.rank}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {entry.username || `User ${entry.userId}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">{entry.score}</p>
                          <p className="text-sm text-gray-600">points</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'achievements' && (
              <div>
                {achievements.length === 0 ? (
                  <div className="bg-white rounded-2xl border-2 border-gray-200 p-12 text-center">
                    <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No achievements yet</h3>
                    <p className="text-gray-600">Complete challenges to earn your first achievement</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {achievements.map((achievement: any) => (
                      <div
                        key={achievement.id}
                        className="bg-white rounded-2xl p-6 border-2 border-gray-200 text-center hover:border-dropsugar-green transition-all shadow-sm"
                      >
                        <div className="text-6xl mb-4">{achievement.achievementIcon ?? '🏆'}</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{achievement.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                        {achievement.unlockedAt && (
                          <p className="text-xs text-gray-500">
                            Earned {new Date(achievement.unlockedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
